// data structure

import { nextLevelName } from '../helpers/hierarchy'
import { nextBorderStyle } from './borderStyle'
import { nextColor, nextDarkColor } from './lineColors'

/**
 * @typedef {import('../types/plottr').CharactersPlottrFileEntry} Character
 * @typedef {import('../types/plottr').FamilyTreeStagePosition} FamilyTreeStagePosition
 * @typedef {import('../types/plottr').ImportModalUiData} ImportModalUiData
 * @typedef {import('../types/plottr').AudioClipRecord} AudioClipRecord
 * @typedef {import('../types/plottr').Slate} Slate
 * @typedef {import('../types/system').SystemNotifications} SystemNotifications
 * @typedef {import('../index').Book} Book
 * @typedef {import('../index').Place} Place
 * @typedef {import('../index').Note} Note
 * @typedef {import('../index').Beat} Beat
 * @typedef {import('../index').PlottrFileUi} PlottrFileUI
 * @typedef {import('../index').Attributes} Attributes
 * @typedef {import('../index').Category} Category
 * @typedef {import('../index').Tag} Tag
 * @typedef {import('../index').Card} Card
 * @typedef {import('../index').Line} Line
 * @typedef {import('../index').File} File
 * @typedef {import('../index').LegacyCustomAttributes} LegacyCustomAttributes
 * @typedef {import('../index').LegacyCustomAttribute} LegacyCustomAttribute
 */

/**
 * @type {Slate}
 */
export const RCE_INITIAL_VALUE = [{ type: 'paragraph', children: [{ text: '' }] }]

export const series = {
  name: '',
  premise: '',
  genre: '',
  theme: '',
  templates: [],
}

/**
 * @type {Book}
 */
export const book = {
  id: 1,
  title: '',
  premise: '',
  genre: '',
  theme: '',
  templates: [],
  timelineTemplates: [],
  imageId: null,
}

/**
 * @type {Beat}
 */
export const beat = {
  id: 1,
  bookId: 'series',
  position: 0,
  title: 'auto',
  time: 0, // ? can also be a string
  templates: [],
  autoOutlineSort: true,
  fromTemplateId: null,
  expanded: true,
}

/**
 * @type {Beat}
 */
export const chapter = {
  id: 2,
  bookId: 1,
  position: 0,
  title: 'auto',
  time: 0, // ? can also be a string
  templates: [],
  autoOutlineSort: true,
  fromTemplateId: null,
  expanded: true,
}

/**
 * @type {ImportModalUiData['customAttributes']}
 */
export const defualtCheckableLegacyCustomAttributes = {
  characters: [],
  places: [],
  scenes: [],
  lines: [],
  notes: [],
}

/**
 * @typedef {ImportModalUiData}
 */
export const defaultImportModalUiData = {
  books: {},
  notes: [],
  characters: [],
  places: [],
  tags: [],
  customAttributes: {
    characters: [],
    places: [],
    scenes: [],
    lines: [],
    notes: [],
  },
  images: {},
}

/**
 * @typedef {import('../types/plottr').PreviewPanels} PreviewPanels
 */

/**
 * @type {PreviewPanels}
 */
export const previewPanels = {
  panels: [],
  showConfirmModal: false,
}

/**
 * @typedef {import('../types/plottr').QuickEditModal} QuickEditModal
 */

/**
 * @type {QuickEditModal}
 */
export const quickEditModal = {
  open: false,
  id: null,
  kind: null,
}

/**
 * @typedef {import('../types/plottr').Bookmarks} Bookmarks
 */

/**
 * @type {Bookmarks}
 */
export const bookmarks = { items: [], collapsedSection: [] }

/**
 * @typedef {import('../types/plottr').IdeaBoard} IdeaBoard
 */

/**
 * @type {IdeaBoard}
 */
export const ideaBoard = {
  open: false,
  ideas: [],
  pocketIdeasUsed: [],
  modal: {
    open: false,
    id: null,
  },
  confirmModal: {
    open: false,
    id: null,
    isDeleting: false,
    section: null,
    data: null,
  },
  showCardDescription: true,
  shouldShowPanelsWithIdeaBoard: false,
}

/**
 * @type {PlottrFileUI}
 */
export const uiState = {
  currentView: 'timeline',
  currentTimeline: 1, // bookId or 'series'
  timelineIsExpanded: true,
  orientation: 'horizontal',
  darkMode: false, // <- Deprecated, but keep here for a while for backwards compatibility.
  characterSort: 'name~asc',
  characterFilter: {
    book: [],
    category: [],
    tag: [],
  },
  placeSort: 'name~asc',
  placeFilter: {
    book: [],
    tag: [],
    category: [],
  },
  noteSort: 'title~asc',
  noteFilter: {
    book: [],
    tag: [],
    character: [],
    place: [],
    category: [],
  },
  outlineFilter: null,
  timelineFilter: {
    tag: [],
    character: [],
    place: [],
    color: [],
  },
  timelineScrollPosition: {
    x: 0,
    y: 0,
  },
  timeline: {
    pinnedPlotlines: {},
    size: 'large',
    view: 'default',
    actTab: 0,
    focus: [],
    beatToDelete: null,
    contextMenuBeat: null,
    editingBeatId: null,
    beatHeadingTitleBeingEdited: null,
    plotlineTitleBeingEdited: null,
    smallTimelineCardColorSource: 'plotline',
    filterVisible: false,
  },
  searchTerms: {
    notes: null,
    characters: null,
    places: null,
    tags: null,
    outline: null,
    timeline: null,
    ideas: null,
    bookmarks: null,
  },
  attributeTabs: {
    characters: 'all',
  },
  characterTab: {
    selectedCharacter: null,
    focus: [],
    attributesDialogOpen: false,
    categoriesDialogOpen: false,
    editingSelected: false,
    showTemplatePicker: false,
    creating: false,
    templateData: null,
    filterVisible: false,
    sortVisible: false,
    detailsVisible: true,
    characterEditor: {
      deleting: false,
      removing: false,
      removeWhichTemplate: null,
      activeTab: 1,
      showTemplatePicker: false,
    },
    characterSelectedForChangingImage: null,
    relationshipEditorOpen: false,
    view: 'default',
    collapsedCategories: false,
    showAsList: true,
  },
  customAttributeOrder: {
    characters: [],
  },
  cardDialog: {
    isOpen: false,
    cardId: null,
    lineId: null,
    beatId: null,
    deleting: false,
    showColorPicker: false,
    showTemplatePicker: false,
    removing: false,
    removeWhichTemplate: null,
  },
  collaborators: {
    collaborators: [],
    viewers: [],
  },
  bookDialog: {
    isOpen: false,
    bookId: null,
  },
  searchDialog: {
    isOpen: false,
    term: '',
    currentHitIndex: null,
    scanning: false,
    replacing: false,
    replacement: '',
    hitsToReplace: [],
    replaceWord: false,
    jumping: false,
    activeTab: 'search',
  },
  projectTab: {
    focus: [],
  },
  outlineTab: {
    focus: [],
    selectedCard: null,
    cardEditor: {
      editing: null,
    },
    layout: {
      view: 'plan',
      fulltext: {
        showCardTitle: true,
        showBeatTitle: true,
        showLayoutConfig: true,
        showBeatGaps: true,
        cardTitleAlignment: 'left',
        beatTitleAlignment: 'left',
        detailsPinned: false,
        zoom: 1,
        light: '2',
        hoverDetails: {
          lineTitle: null,
          beatTitle: null,
          cardTitle: null,
          cardId: null,
          visible: false,
        },
      },
      card: {
        colorSource: 'plotline',
      },
    },
  },
  noteTab: {
    focus: [],
    selectedNote: null,
    editingSelected: false,
    categoriesDialogOpen: false,
    attributesDialogOpen: false,
    filterVisible: false,
    sortVisible: false,
    showAsList: true,
    collapsedCategories: false,
  },
  placeTab: {
    focus: [],
    selectedPlace: null,
    attributeDialogOpen: false,
    editingSelected: false,
    categoriesOpen: false,
    filterVisible: false,
    sortVisible: false,
    showAsList: true,
    collapsedCategories: false,
  },
  tagTab: {
    focus: [],
    selectedTag: null,
    editingSelectedTab: false,
  },
  outlineScrollPosition: 0,
  templateModal: {
    expanded: false,
  },
  actConfigModal: {
    open: false,
  },
  resturctureTimelineModal: {
    open: false,
  },
  dashboardModal: {
    view: null,
  },
  conflictResolutionModal: {
    open: false,
  },
  importModal: {
    type: null,
    data: null,
    bookData: null,
    open: false,
    showProRecentFiles: false,
  },
  familyTree: {
    currentFamilyTree: 0,
    selectedRelationshipType: null,
    showColourPicker: false,
    stagePositions: {},
    idOfTreeWhoseNameIsBeingEdited: null,
  },
  documentation: {
    documentationType: 'advanced-export',
    open: false,
  },
  outlinePlanScrollPosition: 0,
  attributesDialogIsOpen: false,
  showPreviewPanels: true,
  previewPanels,
  quickEditModal,
  bookmarks,
  ideaBoard,
  theme: null,
  mobileControlsModal: {
    open: false,
  },
}

/**
 * @type {FamilyTreeStagePosition}
 */
export const initialFamilyTreePosition = {
  scale: 1,
}

/**
 * @type {PlottrFileUI}
 */
export const ui = {
  ...uiState,
  collaborators: {
    collaborators: [],
    viewers: [],
  },
}

/**
 * @type {Attributes}
 */
export const attributes = {
  characters: [],
}

/**
 * @type {SystemNotifications}
 */
export const notifications = {
  message: null,
  timeout: null,
  toast: {
    cardAction: null,
    newBookId: null,
    visible: false,
    lineAction: null,
  },
}

/**
 * @type {File}
 */
export const file = {
  fileName: '',
  loaded: false,
  dirty: false,
  version: 'UNSET',
}

/**
 * @type {Character}
 */
export const character = {
  id: 1,
  name: '',
  description: '',
  notes: RCE_INITIAL_VALUE,
  color: null,
  cards: [],
  noteIds: [],
  templates: [],
  tags: [],
  categoryId: null,
  imageId: null,
  bookIds: [],
  position: 0,
  attributes: [],
  audioClipId: null,
}

export const categories = {
  characters: [],
  places: [],
  notes: [],
  tags: [],
}

/**
 * @type {Category}
 */
export const category = {
  id: 0,
  name: '',
  position: 0,
}

/**
 * @type {Omit<Place, 'notes'> & { notes: Slate }}
 */
export const place = {
  id: 1,
  name: '',
  description: '',
  notes: RCE_INITIAL_VALUE,
  color: null,
  cards: [],
  noteIds: [],
  templates: [],
  tags: [],
  imageId: null,
  audioClipId: null,
  bookIds: [],
  position: 0,
  categoryId: null,
}

/**
 * @type {Tag}
 */
export const tag = {
  id: 1,
  title: '',
  categoryId: null,
  color: null,
}

/**
 * @type {Card}
 */
export const card = {
  id: 1,
  lineId: 0,
  beatId: 0,
  bookId: null,
  positionWithinLine: 0,
  positionInBeat: 0,
  title: '',
  description: RCE_INITIAL_VALUE,
  tags: [],
  characters: [],
  places: [],
  templates: [],
  imageId: null,
  audioClipId: null,
  fromTemplateId: null,
  color: null,
  notes: [],
}

/**
 * @type {Line}
 */
export const line = {
  id: 1,
  bookId: 1,
  color: '#6cace4',
  title: '',
  position: 0,
  characterId: null,
  expanded: null,
  fromTemplateId: null,
  isPinned: false,
  pinnedPosition: null,
}

/**
 * @type {'series'}
 */
const SERIES_ID = 'series'

/**
 * @type {Line}
 */
export const seriesLine = {
  id: 2,
  bookId: SERIES_ID,
  color: '#6cace4',
  title: '',
  position: 0,
  characterId: null,
  expanded: null,
  fromTemplateId: null,
  isPinned: false,
  pinnedPosition: null,
}

/**
 * @type {LegacyCustomAttributes}
 */
export const customAttributes = {
  characters: [],
  places: [],
  scenes: [],
  lines: [],
  notes: [],
}

/**
 * @type {LegacyCustomAttribute}
 */
export const attribute = {
  name: '',
  type: 'text',
}

/**
 * @type {Note}
 */
export const note = {
  id: 1,
  title: '',
  content: RCE_INITIAL_VALUE,
  categoryId: null,
  tags: [],
  characters: [],
  places: [],
  lastEdited: null,
  templates: [],
  imageId: null,
  audioClipId: null,
  bookIds: [],
  position: 0,
}

export const image = {
  id: 1,
  name: '',
  path: '',
  data: '',
}

/**
 * @type {AudioClipRecord}
 */
export const audioClip = {
  id: 1,
  name: '',
  path: '',
  data: '',
}

// example template for reference
// NOT exported
// const templates = [
//   { id: 'ch1', version: '2020.3.4', attributes: [{ name: 'Motivation', type: 'text', value: '' }] },
// ]

/**
 * @typedef {import('../types/plottr').HierarchyLevel} HierarchyLevel
 */

/**
 * @return {HierarchyLevel}
 */
export const hierarchyLevel = () => {
  return {
    name: nextLevelName('default'),
    level: 0,
    autoNumber: true,
    textSize: 24,
    borderStyle: nextBorderStyle(0),
    backgroundColor: 'none', // Same as app canvas
    textColor: nextColor('default'),
    borderColor: nextColor(0),
    dark: {
      borderColor: nextDarkColor(0),
      textColor: nextDarkColor(0),
    },
    light: {
      borderColor: nextColor(0),
      textColor: nextColor('default'),
    },
  }
}

/**
 * @typedef {import('../types/plottr').FeatureFlags} FeatureFlags
 */

/**
 * @type {FeatureFlags}
 */
export const featureFlags = {}

export const tour = {
  showTour: false,
  // feature:{
  //   name:'',
  //   id:0,
  //   endstep:null
  // }, // XXXXXX use this blank object once the user can select the feature to tour
  feature: {
    name: 'acts',
    id: 1,
    endStep: 8,
  },
  run: true,
  //continuous: true, // won't they all be either continuous or not? - may need to include a continuous key/value in the 'feature' object above depending on which feature, some may not be continuous
  stepIndex: 0,
  steps: [],
  loading: false,
  action: 'start',
  transitioning: false,
  b2bTransition: false,
  toursTaken: {},
  // key: new Date(), // may need this to restart tours when more tours are added
}

/**
 * @param {Array<HierarchyLevel>} allHierarchyLevels
 * @return {HierarchyLevel}
 */
export const newHierarchyLevel = (allHierarchyLevels) => {
  return {
    ...hierarchyLevel(),
    name: nextLevelName(allHierarchyLevels.length),
    level: 0,
    borderStyle: nextBorderStyle(allHierarchyLevels.length),
    textColor: nextColor(allHierarchyLevels.length),
    borderColor: nextColor(allHierarchyLevels.length),
    dark: {
      textColor: nextDarkColor(allHierarchyLevels.length),
      borderColor: nextDarkColor(allHierarchyLevels.length),
    },
    light: {
      textColor: nextColor(allHierarchyLevels.length),
      borderColor: nextColor(allHierarchyLevels.length),
    },
  }
}

/**
 * @typedef {import('../types/plottr').FamilyTree} FamilyTree
 */

/**
 * @type {FamilyTree}
 */
export const emptyFamilyTree = {
  id: 0,
  name: '',
  stage: {
    scale: [1, 1],
    x: 0,
    y: 0,
  },
  members: {},
  backgroundImageId: null,
  backgroundColor: null,
  backgroundImageTranslation: null,
  relationshipConfiguration: {
    Parent: {
      type: 'parent',
      name: 'Parent',
      colour: '#e5554f',
      lineStyle: 'SOLID',
    },
    Spouse: {
      type: 'sibling',
      name: 'Spouse',
      colour: '#6cace4',
      lineStyle: 'SOLID',
    },
    Sibling: {
      type: 'sibling',
      name: 'Sibling',
      colour: '#6cace4',
      lineStyle: 'SOLID',
    },
    Child: {
      type: 'child',
      name: 'Child',
      colour: '#78be20',
      lineStyle: 'SOLID',
    },
  },
}
